import { api } from "./api.ts";

import type {
  ClaimsTransmissionHistoryType,
  ClaimsDataType,
  ValidationDetailType,
  CMPAlert,
  PaginatedResponse,
} from "types/DataTypes";

type CreateClaimArgs = {
  data: { icn: string; rre: string };
};

type GetClaimsArgs = {
  searchTerm?: string;
  filters?: Record<string, string | string[]>;
};

type GetClaimByIdArgs = {
  id: string;
};

type PatchClaimArgs = {
  id: string;
  data: Partial<ClaimsDataType>;
  useDetail?: boolean;
};

type ArchiveClaimArgs = {
  claimId: number;
};

type ResolveClaimCmsErrorArgs = {
  claimId: number;
  data: unknown;
};

type GetClaimCMPAlertsArgs = {
  claimId: number;
};

type GetMatchingClaimsForClaimantArgs =
  | { claimantId: string | number }
  | {
      ssn?: string;
      firstName?: string;
      lastName?: string;
      birthDate?: string;
    };

export type Section111ClaimantMatch = {
  id: number;
  rre_name: string;
  rre_rre_id: string;
  injured_party_first_name: string | null;
  injured_party_last_name: string | null;
  injured_party_middle_initial: string | null;
  injured_party_ssn: string | null;
  injured_party_hicn: string | null;
  injured_party_dob: string | null;
  gender: string | null;
};

export const claimsAPI = api.injectEndpoints({
  endpoints: (build) => ({
    createClaim: build.mutation<ClaimsDataType, CreateClaimArgs>({
      query: ({ data }) => ({
        url: `api/v1/claims/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Claims"],
    }),
    getClaims: build.query<PaginatedResponse<ClaimsDataType>, GetClaimsArgs>({
      query: ({ searchTerm, filters = {} }) => ({
        url: `api/v1/claims/`,
        method: "GET",
        params: searchTerm
          ? { filter: searchTerm, ...filters }
          : { ...filters },
      }),
      providesTags: ["Claims"],
    }),
    getClaimById: build.query<ClaimsDataType, GetClaimByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/claims/${id}/`,
        method: "GET",
      }),
      transformResponse: (response: ClaimsDataType) => {
        if (response.tags && Array.isArray(response.tags)) {
          // @ts-expect-error TODO: fix this
          response.tags = response.tags.map(
            (tag: { id: number; name: string }) => ({
              value: tag.id,
              label: tag.name,
            }),
          );
        }
        return response;
      },
      providesTags: (result, error, { id }) => {
        if (result) {
          return [{ type: "Claim", id }];
        }
        if (error) {
          // todo handle error in toast or specific claim invalidation
          console.error("Error fetching claim:", error);
        }
        return [];
      },
    }),
    patchClaimById: build.mutation<ClaimsDataType, PatchClaimArgs>({
      query: ({ id, data, useDetail }) => ({
        url: `api/v1/claims/${id}/?detail=${useDetail ? useDetail : false}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => {
        if (result) {
          return [
            { type: "Claims" },
            { type: "ClaimValidations" },
            { type: "Claim", id },
            { type: "ClaimAuditHistory", id },
          ];
        }
        if (error) {
          // todo handle error in toast or specific claim invalidation
          console.error("Error patching claim:", error);
        }
        return [];
      },
    }),
    putClaimById: build.mutation<ClaimsDataType, PatchClaimArgs>({
      query: ({ id, data, useDetail }) => ({
        url: `api/v1/claims/${id}/?detail=${useDetail ? useDetail : false}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => {
        if (result) {
          return [
            { type: "Claims" },
            { type: "ClaimValidations" },
            { type: "Claim", id },
            { type: "ClaimAuditHistory", id },
          ];
        }
        if (error) {
          // todo handle error in toast or specific claim invalidation
          console.error("Error putting claim:", error);
        }
        return [];
      },
    }),
    getClaimValidations: build.query<ValidationDetailType[], GetClaimByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/claims/${id}/validations/`,
        method: "GET",
      }),
      providesTags: ["ClaimValidations"],
    }),
    getClaimAuditHistory: build.query<unknown, GetClaimByIdArgs>({
      query: ({ id }) => ({
        url: `/api/v1/claims/${id}/audit_history/`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [{ type: "ClaimAuditHistory", id: arg.id }];
        }
        if (error) {
          // todo handle error in toast or specific claim invalidation
          console.error("Error fetching claim audit history:", error);
        }
        return [];
      },
    }),
    getClaimTransmissionHistory: build.query<
      ClaimsTransmissionHistoryType[],
      GetClaimByIdArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/claims/${id}/cms_transmission_logs/`,
        method: "GET",
      }),
    }),
    getClaimQueryResponses: build.query<unknown, GetClaimByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/claims/${id}/query_responses/`,
        method: "GET",
      }),
    }),
    archiveClaim: build.mutation<void, ArchiveClaimArgs>({
      query: ({ claimId }) => ({
        url: `api/v1/claims/${claimId}/archive`,
        method: "PATCH",
      }),
      invalidatesTags: ["Claims"],
    }),
    resolveClaimCmsError: build.mutation<unknown, ResolveClaimCmsErrorArgs>({
      query: ({ claimId, data }) => ({
        url: `api/v1/claims/${claimId}/acknowledge_cms_errors`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Claim", "ClaimValidations"],
    }),
    getClaimCMPAlerts: build.query<CMPAlert[], GetClaimCMPAlertsArgs>({
      query: ({ claimId }) => ({
        url: `api/v1/claims/${claimId}/calculate_cmp_alerts/`,
        method: "GET",
      }),
    }),
    getMatchingClaimsForClaimant: build.query<
      Section111ClaimantMatch[],
      GetMatchingClaimsForClaimantArgs
    >({
      query: (args) =>
        "claimantId" in args
          ? {
              url: `api/v1/claims/matching_claimant/`,
              method: "GET",
              params: { claimant_id: args.claimantId },
            }
          : {
              url: `api/v1/claims/matching_claimant/`,
              method: "GET",
              params: {
                ssn: args.ssn,
                first_name: args.firstName,
                last_name: args.lastName,
                birth_date: args.birthDate,
              },
            },
      providesTags: (result, error, args) =>
        "claimantId" in args
          ? [{ type: "Section111ClaimantMatches", id: args.claimantId }]
          : [{ type: "Section111ClaimantMatches" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetClaimsQuery,
  useGetClaimByIdQuery,
  useLazyGetClaimByIdQuery,
  usePatchClaimByIdMutation,
  usePutClaimByIdMutation,
  useGetClaimAuditHistoryQuery,
  useGetClaimTransmissionHistoryQuery,
  useGetClaimQueryResponsesQuery,
  useGetClaimValidationsQuery,
  useCreateClaimMutation,
  useArchiveClaimMutation,
  useResolveClaimCmsErrorMutation,
  useGetClaimCMPAlertsQuery,
  useGetMatchingClaimsForClaimantQuery,
} = claimsAPI;
